import { PercentPipe } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { Observable, Subscription } from 'rxjs';
import { first, tap } from 'rxjs/operators';

import { AuthService } from '../../../auth/auth.service';
import { takeUntilDestroy } from '../../../common/take-until-destroy.decorator';
import { WidgetConfiguration } from '../../../widget/config.widget.model';
import { CMPS_WIDGET_CONFIG } from '../../../widget/widget.metadata';
import { TranslationKeyMap } from '../../coaching-insight.i18n';
import { OldOaWidgetTypeId } from '../../coaching-insight.module';
import { Dimensions } from '../elastic-element.directive';
import {
    ColumnConfigItem,
    Icon,
    PersonOfInterest,
} from '../models/coaching.global.model';
import {
    ACTIVE_TAB_VIEW,
    CoachingInsightSessionKeys,
    OaPerson,
    OaPopulation,
    Opportunity,
    SHOW_INACTIVE_PERFORMERS,
} from '../models/oa.model';
import { SortDirection } from '../models/sorting.model';
import { CoachingInsightWidgetService } from '../services/coaching-insight.widget.service';
import { ElasticUIService } from '../services/elastic-ui.service';
import { PersonOfInterestService } from '../services/person-of-interest.service';
import { TeamOAService } from '../services/team-oa.service';

import { clearSessionStorage } from './coachingInsight-session-storage-functions';

// maintain a list of Team Member Metrics
@Component({
    selector: 'cmps-team-oa-metrics',
    templateUrl: './team-oa-metrics.component.html',
    // changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [PercentPipe],
})
export class TeamOAMetricsComponent implements OnChanges, OnInit, OnDestroy {
    @Input() selectedUser: PersonOfInterest;
    @Input() coachingInsightUserId: number;
    @Input() oaPeople: OaPopulation = {
        team: [],
        others: [],
        self: null,
    };

    @Input() active: string[];
    @Input() filtered: boolean;

    @Output() metricsRendered: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    @Output() reorderedPeople: EventEmitter<SortDirection> =
        new EventEmitter<SortDirection>();

    @Output() loadedPerformers: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    @Output() destroyedTeamOA: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    @Output() toggledDisplay: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    @Output() filterToggled: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    @ViewChild('oatooltip') oatooltip: TemplateRef<any>;
    @ViewChild('nonkpitooltip') nonkpitooltip: TemplateRef<any>;
    @ViewChild('thConatiner', { static: true }) thConatiner;
    @ViewChild('oaFilterWindow') oaFilterWindow: ElementRef;
    @ViewChild('legendButton', { static: true }) legendButton;

    @ViewChild('activeRowDetail') activeRowDetail: ElementRef;

    i18n = TranslationKeyMap;
    hasPendingData = false;

    public columns: Observable<ColumnConfigItem[]>;
    public activecolumns: Observable<number>;

    public currentViewMemberId: number = null;

    public activetab: string = 'tab-performance';

    ACTIVE_TAB_VIEW = ACTIVE_TAB_VIEW;

    activeView: ACTIVE_TAB_VIEW;
    activeviewsubs: Subscription;
    activeMember: Observable<OaPerson>;
    activeMetric: Observable<Opportunity>;
    dimensions: Observable<Dimensions[]>;

    activeheaders = null;

    moreOAMetricsOpened = false;
    moreNonOAMetricsOpened = false;
    rowExpanded = false;

    showLegend = false;

    oametrictypeSelected = '';
    oafilterSortSelected: string;

    ascendingSort = false;

    openNonKPI = false;
    openPerformerOnly = false;
    isNonKPI = false;

    builtFilter = true;

    teamcount: number;
    othercount: number;

    filtersub: Subscription;

    oaLegend: any;

    showInActivePerformers = false;
    activePerformersObs: Observable<boolean>;
    toggleActiveSub: Subscription;

    tableViewIsDefault = true;
    columnsSub: Subscription;

    defaultCols: string;

    activeMemberSub: Subscription;
    activeMetricSub: Subscription;
    _activeMember: OaPerson;
    _activeMetric: Opportunity;

    /** @private */ readonly _toggleActivePerformers: string;
    /** @private */ _isNonKpi: boolean;

    /** @private */
    _ref: CmpsDialogRef<TeamOAMetricsComponent, void>;

    constructor(
        private readonly _viewContainerRef: ViewContainerRef,
        private readonly _coachingInsightService: CoachingInsightWidgetService,
        private readonly _personOfInterestService: PersonOfInterestService,
        private readonly _teamOAService: TeamOAService,
        private readonly _authService: AuthService,
        private readonly _elasticService: ElasticUIService,
        private readonly _router: Router,
        // private cdRef: ChangeDetectorRef
        @Optional()
        @Inject(CMPS_WIDGET_CONFIG)
        private readonly _config: WidgetConfiguration,
        private readonly _dialog: CmpsDialog,
    ) {
        this._toggleActivePerformers = `tap${(Math.random() + 1)
            .toString(36)
            .substring(7)}`;
        this.activeviewsubs = this._teamOAService.activeView.subscribe(
            (data) => {
                if (data != null) {
                    this.activeView = data;
                }
            },
        );

        this.filtersub = this._teamOAService.filteredPerformers.subscribe(
            (data) => {
                if (data != null) {
                    this.builtFilter = false;

                    setTimeout(() => {
                        if (this.oaPeople.team) {
                            this.teamcount = this.oaPeople.team.filter(
                                (t) => t.display === true,
                            ).length;
                        }
                        if (this.oaPeople.others) {
                            this.othercount = this.oaPeople.others.filter(
                                (t) => t.display === true,
                            ).length;
                        }

                        this.builtFilter = true;
                    }, 1);
                }
            },
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get user() {
        return this._authService.user;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.defaultCols = JSON.stringify(
            this._teamOAService.getFreshColumnConfig(),
        );

        this.builtFilter = true;
        this.columns = this._teamOAService.columns;
        // eslint-disable-next-line no-return-assign
        this.columns
            .pipe(
                first(),
                tap((data) => (data[1].icon[0].state = true)),
            )
            .subscribe();

        // if the table view changed
        this.columnsSub = this.columns.subscribe((g) => {
            this.testTableView(g);
        });

        this.activecolumns = this._teamOAService.activemetriccolumns;
        this.activeheaders = document.querySelectorAll('.team-oa-header');
        this.activeMember = this._teamOAService.activeMember;
        this.activeMetric = this._teamOAService.activeMetric;

        this.currentViewMemberId =
            this._personOfInterestService.getCurrentViewId();

        this.teamcount = this.oaPeople.team.length;
        this.othercount = this.oaPeople.others.length;

        this.activePerformersObs = this._teamOAService.showActivePerformers;

        this.toggleActiveSub = this.activePerformersObs.subscribe((g) => {
            this.showInActivePerformers = g;
            this.testTableView();
        });

        if (this._config && this._config.typeId !== OldOaWidgetTypeId) {
            this.activeMetricSub = this.activeMetric.subscribe((opp) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (opp && opp.metricId) {
                    this._activeMetric = opp;

                    this.navigateToCoachingDetail(
                        this._activeMember,
                        this._activeMetric,
                    );
                }
            });
            this.activeMemberSub = this.activeMember.subscribe((member) => {
                if (member) {
                    this._activeMember = member;

                    this.navigateToCoachingDetail(
                        this._activeMember,
                        this._activeMetric,
                    );
                }
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setOpenWithoutOpp(performerOnly = true) {
        if (performerOnly === true) {
            this.openNonKPI = false;
            this.openPerformerOnly = true;
        } else {
            this.openPerformerOnly = false;
            this.openNonKPI = true;
            this._isNonKpi = true;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    navigateToCoachingDetail(member: OaPerson, opp: Opportunity) {
        if (!member || member.userId == null) return;
        if (
            (!opp || !opp.metricId) &&
            !this.openNonKPI &&
            !this.openPerformerOnly
        )
            return;

        // not necessarily the current open OA,
        // but the root Coaching Insight user;
        // either the logged in user, or the Searched user
        /** if (this.coachingInsightUserId) {
            sessionStorage.setItem('coachingInsight-userId', this.coachingInsightUserId.toString());
        } */

        const cmtIds = opp
            ? opp.commitments.map((e) => e.commitmentId).join('-')
            : undefined;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._router.navigate(['coaching-detail', member.userId], {
            queryParams: {
                metricId: opp ? opp.metricId : undefined,
                fromOA: true,
                cmtIds,
                isNonKpi: this._isNonKpi,
            },
        });

        if (this.activeMetricSub) {
            this.activeMetricSub.unsubscribe();
        }

        if (this.activeMemberSub) {
            this.activeMemberSub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(args) {
        // eslint-disable-next-line no-prototype-builtins
        if (args.hasOwnProperty('selectedUser')) {
            const view = this._teamOAService.decideInitialView(this.oaPeople);
            this._teamOAService.setView(
                view,
                this.selectedUser.userId || this.selectedUser.value,
            );
        }
    }

    testTableView(g?: ColumnConfigItem[]): void {
        this.tableViewIsDefault = false;
        const obj = JSON.parse(
            sessionStorage.getItem(
                CoachingInsightSessionKeys.COLUMN_CONFIG_ITEMS,
            ),
        );
        if (!obj) {
            this.tableViewIsDefault = true;
            return;
        }
        // const currentCols = JSON.stringify(g);

        if (!g) {
            if (this.showInActivePerformers !== SHOW_INACTIVE_PERFORMERS) {
                this.tableViewIsDefault = false;
            }
            return;
        }

        const b = JSON.parse(JSON.stringify(g));

        const h = b.map((a) => {
            a.cacheStyleProps = undefined;
            return a;
        });

        const currentCols = JSON.stringify(h);

        if (
            (currentCols &&
                this.defaultCols &&
                currentCols !== this.defaultCols) ||
            this.showInActivePerformers !== SHOW_INACTIVE_PERFORMERS
        ) {
            this.tableViewIsDefault = false;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleScoreTooltip(tooltip: any, member: OaPerson) {
        member && !tooltip.isOpen()
            ? tooltip.open({ member })
            : tooltip.close();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    openLegendModal(templateref) {
        this._ref = this._dialog.open(templateref, {
            viewContainerRef: this._viewContainerRef,
        });
        this._ref.result.pipe(takeUntilDestroy(this)).subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    filterToggledHandler(evt: boolean) {
        if (typeof evt === 'boolean') {
            this.moreOAMetricsOpened = evt;
            this.filterToggled.emit(evt);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    handleFiltered(evt) {
        this.reorderedPeople.emit(evt);
    }

    hasToggle(th: ColumnConfigItem): boolean {
        // eslint-disable-next-line no-prototype-builtins
        return th.hasOwnProperty('toggle');
    }

    toggle(event, icon: Icon): void {
        if (icon.action) {
            this[icon.action].apply(
                this,
                icon.actionParams instanceof Array
                    ? [...icon.actionParams, icon]
                    : icon.actionParams === null
                    ? [icon]
                    : icon.actionParams,
            );
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    sortTeamByScore(icon?: Icon) {
        const newdir = this._teamOAService.sortTeamByScore(null, icon);
        this.reorderedPeople.emit(newdir);
    }

    toggleColumnByIndex(idx: number): void {
        const colconfig = this._teamOAService.toggleColumn(idx, true);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (colconfig && colconfig.bindToId !== null) {
            this._coachingInsightService.updateSubmenu(colconfig.bindToId);
        }
    }

    compressColumnByIndex(idx: number, effect?: string): void {
        this._teamOAService.compressColumn(idx, effect);
        this._teamOAService.updateActiveColumns();
    }

    showDefaultView(): void {
        clearSessionStorage();
        setTimeout(() => {
            const cols = this._teamOAService.showDefaultColumns();
            this._teamOAService.defaultActivePerformersView();
            this._coachingInsightService.resetSubmenu(cols);
            this._teamOAService.updateActiveColumns();
            this._teamOAService.resetColumnIcons();
            this._teamOAService.toggleAllMetricFromGlobalFilter(false);
            this._teamOAService.showDefaultColumns();
        }, 300);
    }

    toggleShowActivePerformers($event: Event): void {
        this.scrollToExpanded();
        const target = $event.target as HTMLInputElement;
        target.checked = this._teamOAService.toggleShowActivePerformers(
            target.checked,
        );
        this.toggledDisplay.emit(target.checked);

        // ONLY ON CLICK
        // for this scenario, try toggling Missed Opps Column in case some performers have missed opps
        // that would change the display of missed opps column.
        this._teamOAService.toggleMissedOppsColumn();
        this._teamOAService.updateActiveColumns();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    scrollToExpanded() {
        setTimeout(() => {
            // https://plainjs.com/javascript/styles/get-the-position-of-an-element-relative-to-the-document-24/
            const rect =
                this.legendButton.nativeElement.getBoundingClientRect();
            const scrollLeft =
                window.pageXOffset || document.documentElement.scrollLeft;
            const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            const offset = {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                top: rect.top + scrollTop,
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                left: rect.left + scrollLeft,
            };

            const navbarheight = document
                .querySelector('cmps-nav>.navbar.fixed-top')
                .getBoundingClientRect().height;
            const top = offset.top - navbarheight - 17;
            try {
                window.scrollTo({
                    left: 0,
                    top,
                    behavior: 'smooth',
                });
            } catch (e) {
                window.scrollTo(0, top);
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.activeviewsubs) {
            this.activeviewsubs.unsubscribe();
        }
        if (this.toggleActiveSub) {
            this.toggleActiveSub.unsubscribe();
        }

        const p = this._personOfInterestService.getFavouriteView();

        if (p && p.userId !== this.user.id) {
            this.destroyedTeamOA.emit(true);
        }

        this._teamOAService.unsetActives();
        this._elasticService.destroy();

        if (this.activeMetricSub) {
            this.activeMetricSub.unsubscribe();
        }
        if (this.activeMemberSub) {
            this.activeMemberSub.unsubscribe();
        }
    }
}
